// status-operating-table.ts
//
// P13 Agent C: the operator-facing table `avatar autopilot status` renders —
// one row per ticket (id/kind/slice/status/attempt current-of-total/last
// verdict/running-since) plus a mission summary line (status == the closest
// thing this codebase has to a "phase" — mission_status_changed already
// carries values like "blocked:quiet_hours" — gates awaited (+request_id),
// quota wake_at from quota-sleep.json). Pure data-shaping: all I/O (ticket
// frontmatter, attempt-state.json, quota-sleep.json, gates.json,
// effective-config.json) happens in status-renderer.ts's readStatusSnapshot,
// this module only turns already-read values into rows — keeps it testable
// without tmpdir fixtures for every row-shape case.
import type { GateFile, GateId } from "./gate-registry.js";
import type { TicketVerdictRecord } from "./mission-state-reducer.js";
import type { QuotaSleepState } from "./quota-sleep-scheduler.js";
import type { AttemptStateFile } from "./supervisor-attempt-state.js";

export interface TicketOperatingRow {
  id: string;
  kind: string;
  sliceId: string;
  status: string;
  /** 1-based "current attempt" — physical_runs + 1 when the ticket is still open, else physical_runs (no attempt is "in flight" once terminal). */
  attemptCurrent: number;
  /** Configured ceiling (max_counted_failures) — undefined when effective-config.json wasn't readable (best-effort). */
  attemptTotal: number | undefined;
  lastVerdictCategory: string | undefined;
  lastVerdictReasons: string[];
}

export interface TicketForRow {
  id: string;
  kind: string;
  slice_id: string;
}

export function buildTicketOperatingRows(
  tickets: TicketForRow[],
  ticketStatus: Record<string, string>,
  attemptState: AttemptStateFile,
  ticketLastVerdict: Record<string, TicketVerdictRecord>,
  maxCountedFailures: number | undefined,
): TicketOperatingRow[] {
  return tickets.map((ticket) => {
    const status = ticketStatus[ticket.id] ?? "todo";
    const retryState = attemptState.tickets[ticket.id];
    const physicalRuns = retryState?.physical_runs ?? 0;
    // A ticket still open (todo/in_progress/blocked-but-retryable) is ABOUT
    // to run attempt physicalRuns+1 next; a terminal ticket (done/blocked
    // for good/cancelled/superseded) already used its last physical_runs
    // value as its final attempt number — no "next" attempt exists.
    const isOpen = status === "todo" || status === "in_progress";
    const attemptCurrent = isOpen ? physicalRuns + 1 : physicalRuns;
    const verdict = ticketLastVerdict[ticket.id];
    return {
      id: ticket.id,
      kind: ticket.kind,
      sliceId: ticket.slice_id,
      status,
      attemptCurrent,
      attemptTotal: maxCountedFailures,
      lastVerdictCategory: verdict?.category,
      lastVerdictReasons: verdict?.reasons ?? [],
    };
  });
}

export interface PendingGateRequest {
  gate: GateId;
  request_id: string | undefined;
}

export interface MissionOperatingSummary {
  missionId: string | undefined;
  /** Mission's own status string (RUNNING / blocked:<code> / DONE / "paused" / ...) — the closest thing to a "phase" this codebase journals. */
  status: string | undefined;
  /** Every gate (g0..g3 order) currently "pending", with its request_id — [] when none. */
  pendingGates: PendingGateRequest[];
  /** quota-sleep.json's wake_at, when a SLEEPING episode is on file — undefined otherwise. */
  quotaWakeAt: string | undefined;
  /** Phase 05 Req 8: attempts counted in `attempts_total` that never emitted `attempt_usage_recorded` (killed/SIGINT'd/unlocked) — 0 when every attempt has usage. */
  attemptsWithoutUsage: number;
}

const GATE_ORDER: GateId[] = ["g0", "g1", "g2", "g3"];

export function listPendingGateRequests(gates: GateFile | undefined): PendingGateRequest[] {
  if (gates === undefined) return [];
  return GATE_ORDER.filter((id) => gates[id]?.status === "pending").map((gate) => ({
    gate,
    request_id: gates[gate]?.request_id,
  }));
}

export function formatPendingGateRequests(pending: readonly PendingGateRequest[]): string {
  return pending.map((p) => `${p.gate} [${p.request_id ?? "?"}]`).join(", ");
}

export function buildMissionOperatingSummary(
  missionId: string | undefined,
  status: string | undefined,
  gates: GateFile | undefined,
  quotaState: QuotaSleepState | undefined,
  attemptsWithoutUsage = 0,
): MissionOperatingSummary {
  return {
    missionId,
    status,
    pendingGates: listPendingGateRequests(gates),
    quotaWakeAt: quotaState?.wake_at,
    attemptsWithoutUsage,
  };
}

/** Req 7 (D8/B15): shared by `renderStatusTableText` and `autopilot.ts`'s fallback line (DRY) — `"paused"` gets the resume hint appended. */
export function formatMissionStatusLine(
  missionId: string | undefined,
  status: string | undefined,
): string {
  const base = `Mission ${missionId ?? "(chưa có)"}: ${status ?? "(unknown)"}`;
  return status === "paused" ? `${base} — chạy avatar autopilot resume` : base;
}

function padRight(value: string, width: number): string {
  return value.length >= width ? value : value + " ".repeat(width - value.length);
}

/**
 * Compact terminal table for `avatar autopilot status` (non-`--json`,
 * non-HTML output) — one line per ticket, column-aligned, plus a mission
 * summary line underneath (spec §15: "in bảng gọn ... dưới bảng là dòng
 * mission"). No color/ANSI — terminal-logger.ts's `log.info` handles that
 * layer; this function only returns plain text lines.
 */
export function renderStatusTableText(
  rows: TicketOperatingRow[],
  summary: MissionOperatingSummary,
): string {
  const missionLine =
    formatMissionStatusLine(summary.missionId, summary.status) +
    (summary.pendingGates.length > 0
      ? ` · gate chờ: ${formatPendingGateRequests(summary.pendingGates)}`
      : "") +
    (summary.quotaWakeAt !== undefined ? ` · quota wake_at: ${summary.quotaWakeAt}` : "");
  // Phase 05 Req 8: shown right under the mission line whenever cost is
  // missing figures for one or more counted attempts (killed/SIGINT'd/
  // unlocked before ever emitting attempt_usage_recorded).
  const costCaveat =
    summary.attemptsWithoutUsage > 0
      ? `\nchi phí chưa gồm ${summary.attemptsWithoutUsage} attempt bị ngắt (không có usage)`
      : "";

  if (rows.length === 0) {
    return `${missionLine} — chưa có ticket nào.${costCaveat}`;
  }

  const idWidth = Math.max(2, ...rows.map((r) => r.id.length));
  const kindWidth = Math.max(4, ...rows.map((r) => r.kind.length));
  const sliceWidth = Math.max(5, ...rows.map((r) => r.sliceId.length));
  const statusWidth = Math.max(6, ...rows.map((r) => r.status.length));

  const lines = rows.map((r) => {
    const attempt = `${r.attemptCurrent}/${r.attemptTotal ?? "?"}`;
    const verdict =
      r.lastVerdictCategory !== undefined
        ? `${r.lastVerdictCategory}: ${r.lastVerdictReasons.join("; ")}`
        : "";
    return (
      `${padRight(r.id, idWidth)}  ${padRight(r.kind, kindWidth)}  ` +
      `${padRight(r.sliceId, sliceWidth)}  ${padRight(r.status, statusWidth)}  ` +
      `${padRight(attempt, 7)}  ${verdict}`
    ).trimEnd();
  });

  return [...lines, missionLine].join("\n") + costCaveat;
}
